import { Button, Text, Badge, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconUsers, IconPlus } from "@tabler/icons-react";
import { useMemo, useEffect } from "react";
import useEmbaixadoresPessoas from "../../hooks/useEmbaixadoresPessoas";
import EmbaixadorSelectorModal from "../embaixador-selector-modal";

interface EmbaixadorSelectorButtonProperties {
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  label?: string;
  placeholder?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "filled" | "light" | "outline" | "subtle" | "default";
  disabled?: boolean;
  error?: string;
}

const EmbaixadorSelectorButton = ({
  selectedIds,
  onSelectionChange,
  label = "Embaixadores",
  placeholder = "Selecionar embaixadores",
  size = "sm",
  variant = "default",
  disabled = false,
  error
}: EmbaixadorSelectorButtonProperties) => {
  const [opened, { open, close }] = useDisclosure(false);
  const { data: embaixadores = [] } = useEmbaixadoresPessoas();

  const selectedEmbaixadores = useMemo(() => {
    return embaixadores.filter((embaixador) => selectedIds.includes(embaixador.id));
  }, [embaixadores, selectedIds]);

  // Clean up invalid IDs when embaixadores data loads
  useEffect(() => {
    if (embaixadores.length > 0 && selectedIds.length > 0) {
      const validIds = selectedIds.filter((id) => embaixadores.some((embaixador) => embaixador.id === id));
      if (validIds.length !== selectedIds.length) {
        onSelectionChange(validIds);
      }
    }
  }, [embaixadores, selectedIds, onSelectionChange]);

  const selectedCount = selectedEmbaixadores.length;

  return (
    <>
      <Stack gap="xs">
        {label && (
          <Text size="sm" fw={500}>
            {label}
          </Text>
        )}

        <Button
          variant={variant}
          size={size}
          leftSection={<IconUsers size="1rem" />}
          rightSection={
            selectedCount > 0 ? (
              <Badge size="sm" variant="filled">
                {selectedCount}
              </Badge>
            ) : (
              <IconPlus size="1rem" />
            )
          }
          onClick={open}
          disabled={disabled}
          justify="space-between"
          fullWidth
          styles={{
            inner: { justifyContent: "space-between" },
            label: { flex: 1, textAlign: "left" }
          }}
        >
          {selectedCount > 0 ? `${selectedCount} embaixadores selecionados` : placeholder}
        </Button>

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
      </Stack>

      <EmbaixadorSelectorModal
        opened={opened}
        onClose={close}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
      />
    </>
  );
};

export default EmbaixadorSelectorButton;
